import { Button } from "@/components/ui/button";
import { Dialog, DialogClose, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { TFunction } from "i18next";
import { FieldErrors, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Form } from "@/components/ui/form";
import { DateInputField, FormattedNumberInputField } from "@/components/shared/form";
import { convertDecimalToBp } from "@/utils/helpers";
import { useAddLiabilityInsuranceMutationHook } from "@/hooks";
import { useEffect } from "react";

const createLiabilityInsuranceSchema = (t: TFunction) => z.object({
  activationDate: z.date({
    error: t("schemas:form.required"),
  }),
  liabilityInsurance: z.number({
    error: t("schemas:form.required"),
  }).min(0, { message: t("schemas:form.atleast0", { min: 0 }) }).max(100, { message: t("schemas:form.atmax100", { max: 100 }) }),
  professionalAssociation: z.number({
    error: t("schemas:form.required"),
  }).min(0, { message: t("schemas:form.atleast0", { min: 0 }) }).max(100, { message: t("schemas:form.atmax100", { max: 100 }) }),
});

const defaultValues = {
  liabilityInsurance: undefined,
  professionalAssociation: undefined,
  activationDate: undefined,
};

interface AddLiabilityInsuranceDialogProps {
  open: boolean;
  onOpenChange: (_open: boolean) => void;
}

function AddLiabilityInsuranceDialog({ open, onOpenChange }: AddLiabilityInsuranceDialogProps) {
  const { t } = useTranslation(["modals", "common"]);
  const { addLiabilityInsuranceMutation } = useAddLiabilityInsuranceMutationHook();

  const liabilityInsuranceSchema = createLiabilityInsuranceSchema(t);
  const form = useForm<z.infer<typeof liabilityInsuranceSchema>>({
    resolver: zodResolver(liabilityInsuranceSchema),
    defaultValues,
    mode: "onChange",
  });

  // Reset form when dialog opens
  useEffect(() => {
    if (open) {
      form.reset(defaultValues);
    }
  }, [open, form]);

  const onSubmit = (data: z.infer<typeof liabilityInsuranceSchema>) => {
    addLiabilityInsuranceMutation({
      variables: {
        input: {
          activeFrom: data.activationDate,
          liabilityInsurance: convertDecimalToBp(data.liabilityInsurance),
          professionalAssociation: convertDecimalToBp(data.professionalAssociation),
        }
      }
    }).finally(() => {
      onOpenChange(false);
    });
  };

  const onInvalid = (errors: FieldErrors<z.infer<typeof liabilityInsuranceSchema>>) => {
    console.warn(errors);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit, onInvalid)} className="space-y-6" id="add-liability-insurance-form">
            <DialogHeader>
              <DialogTitle>
                {t("liabilityinsurance.title")}
              </DialogTitle>
              <DialogDescription>
                {t("liabilityinsurance.description")}
              </DialogDescription>
            </DialogHeader>
            <div className="flex flex-col gap-4">
              <div className="flex flex-col gap-2">
                <DateInputField
                  form={form}
                  name="activationDate"
                  label={t("liabilityinsurance.inputs.activationdate.label")}
                  className="w-full"
                  allowOlderDates={true}
                />
              </div>

              <FormattedNumberInputField
                control={form.control}
                name="liabilityInsurance"
                label={t("liabilityinsurance.inputs.liabilityinsurance.label")}
                isStandalone={true}
                style="decimal"
                additionalSuffix="%"
                decimalScale={2}
                placeholder={t("liabilityinsurance.inputs.liabilityinsurance.placeholder")}
                className="w-full"
              />
              <FormattedNumberInputField
                control={form.control}
                name="professionalAssociation"
                label={t("liabilityinsurance.inputs.professionalassociation.label")}
                isStandalone={true}
                style="decimal"
                additionalSuffix="%"
                decimalScale={2}
                placeholder={t("liabilityinsurance.inputs.professionalassociation.placeholder")}
                className="w-full"
              />
            </div>
            <DialogFooter>
              <DialogClose asChild>
                <Button variant="outline" type="button">
                  {t("common:buttons.close")}
                </Button>
              </DialogClose>
              <Button type="submit">
                {t("common:buttons.submit")}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}

export default AddLiabilityInsuranceDialog;
